import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  FiActivity,
  FiCalendar,
  FiPieChart,
  FiTrendingUp,
  FiMessageSquare,
  FiUsers,
  FiTarget,
  FiZap,
  FiCheckSquare
} from 'react-icons/fi';
import './LandingPage.css';

const LandingPage = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [selectedCoach, setSelectedCoach] = useState('');

  // Sample coaches list - this would come from Firestore in production
  const coaches = [
    { id: '1', name: 'John Smith' },
    { id: '2', name: 'Sarah Johnson' },
    { id: '3', name: 'Mike Williams' },
    { id: '4', name: 'Emma Davis' }
  ];

  const features = [
    {
      icon: <FiPieChart />,
      title: 'Macro Calculator',
      description: 'Scientifically accurate macro calculations tailored to your goals'
    },
    {
      icon: <FiActivity />,
      title: 'Coach Dashboard',
      description: 'Manage all your clients, programs, and progress in one place'
    },
    {
      icon: <FiCalendar />,
      title: 'Food Logger',
      description: 'Track your nutrition with our comprehensive food database'
    },
    {
      icon: <FiTrendingUp />,
      title: 'Progress Tracking',
      description: 'Visualize your journey with detailed analytics and charts'
    },
    {
      icon: <FiTarget />,
      title: 'Meal Planning',
      description: 'Custom meal plans designed to hit your macro targets'
    },
    {
      icon: <FiZap />,
      title: 'Training Programs',
      description: 'AI-powered program generator for optimal results'
    },
    {
      icon: <FiMessageSquare />,
      title: 'Direct Messaging',
      description: 'Stay connected with your coach or clients in real-time'
    },
    {
      icon: <FiUsers />,
      title: 'Client Management',
      description: 'Seamless client onboarding and progress monitoring'
    },
    {
      icon: <FiCheckSquare />,
      title: 'Habit Tracking',
      description: 'Build consistency with daily habit tracking and accountability'
    }
  ];

  const handleCoachSelection = async () => {
    if (!selectedCoach) {
      alert('Please select a coach');
      return;
    }
    // Link client to coach logic would go here
    console.log('Linking to coach:', selectedCoach);
  };

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <h1 className="hero-title">Your Fitness Journey Starts Here</h1>
          <p className="hero-subtitle">
            A modern, stylish portal empowering coaches to create transformative programs
            and clients to access the tools they need to take their training to the next level
          </p>
          <Link to="/signup" className="btn-primary">
            Get Started
          </Link>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Everything You Need to Succeed</h2>
            <p className="section-subtitle">
              Powerful tools for coaches and clients, all in one platform
            </p>
          </div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Conditional Messaging Section */}
      {currentUser && userProfile && (
        <section className="user-message-section">
          <div className="container">
            {userProfile.role === 'trainer' ? (
              // Coach Message
              <div className="message-card coach-message">
                <div className="message-icon">
                  <FiUsers size={48} />
                </div>
                <h3>Welcome, Coach!</h3>
                <p>
                  Add your clients to get started. Remember to send them registration links
                  to connect them to your coach portal.
                </p>
                <Link to="/unique-physique/trainer-dashboard" className="btn-secondary">
                  Go to Dashboard
                </Link>
              </div>
            ) : (
              // Client Message
              <div className="message-card client-message">
                {userProfile.trainerId ? (
                  <>
                    <div className="message-icon">
                      <FiTarget size={48} />
                    </div>
                    <h3>You're All Set!</h3>
                    <p>
                      Your coach will communicate with you directly and start adding your
                      personalized programs. Check your dashboard to get started.
                    </p>
                    <Link to="/unique-physique/client-dashboard" className="btn-secondary">
                      Go to Dashboard
                    </Link>
                  </>
                ) : (
                  <>
                    <div className="message-icon">
                      <FiUsers size={48} />
                    </div>
                    <h3>Welcome to Unique Physique!</h3>
                    <p>
                      First, choose your coach from the dropdown below. Your coach will then
                      start communicating with you and sending your personal programs.
                    </p>
                    <div className="coach-selector">
                      <select
                        value={selectedCoach}
                        onChange={(e) => setSelectedCoach(e.target.value)}
                        className="coach-dropdown"
                      >
                        <option value="">Select Your Coach</option>
                        {coaches.map((coach) => (
                          <option key={coach.id} value={coach.id}>
                            {coach.name}
                          </option>
                        ))}
                      </select>
                      <button onClick={handleCoachSelection} className="btn-secondary">
                        Confirm Coach
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* CTA Section for Non-logged in users */}
      {!currentUser && (
        <section className="cta-section">
          <div className="container">
            <div className="cta-content">
              <h2>Ready to Transform Your Fitness?</h2>
              <p>Join Unique Physique today and experience the future of personal training</p>
              <div className="cta-buttons">
                <Link to="/signup" className="btn-primary">Sign Up Now</Link>
                <Link to="/calculator" className="btn-outline">Try Macro Calculator</Link>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default LandingPage;
